import { useState, useEffect, FormEvent } from 'react'
import { useSearchParams } from 'react-router-dom'
import { apiClient } from '@/services/api/client'
import { extractErrorMessage } from '@/utils/errorHandler'
import { DirectorRightsEditor } from '@/components/superadmin/DirectorRightsEditor'
import { BulkImportTab } from '@/components/superadmin/BulkImportTab'
import type { ImportExecuteResult } from '@/components/superadmin/BulkImportTab'
import { CheckCircle2, XCircle } from 'lucide-react'
import { ConfirmDeleteModal } from '@/components/teachers/ConfirmDeleteModal'
import { listBackgrounds } from '@/services/api/qualification'
import type { Background } from '@/types/qualification'

interface Subject {
  id: string
  name: string
}

interface UserBackground {
  id: string
  name: string
  is_primary: boolean
}

interface FacultyInfo {
  id: string
  name: string
  code: string | null
  color: string | null
  is_primary: boolean
}

interface User {
  id: string
  email: string
  first_name: string
  last_name: string
  role: string
  roles: string[]
  phone: string | null
  mobile: string | null
  user_code: string | null
  teacher_code: string | null
  is_active: boolean
  created_at: string
  language: string | null
  backgrounds: UserBackground[]
  subjects: Subject[]
  faculties: FacultyInfo[]
  faculty_id: string | null
  faculty_name: string | null
  teacher_id: string | null
  accept_decline_ratio: number
  total_accepts: number
  total_declines: number
  substitution_range_current_semester: number
  // NEU: Timetable indicator fields (only for teachers)
  has_timetable: boolean | null
  timetable_entry_count: number | null
}

interface ApiUsersResponse {
  users: User[]
  total: number
  has_more: boolean
}

interface ImportSession {
  id: string
  target_role: string
  original_filename: string
  status: string
  total_rows: number
  rows_created: number
  rows_updated: number
  rows_skipped: number
  rows_errored: number
  created_at: string
}

interface UserFormData {
  email: string
  first_name: string
  last_name: string
  role: string
  phone: string
  teacher_code: string
  notes?: string
}

const emptyForm: UserFormData = {
  email: '', first_name: '', last_name: '', role: 'teacher', phone: '', teacher_code: '',
}

const ROLE_LABELS: Record<string, string> = {
  ultraadmin: 'Ultraadmin',
  superadmin: 'Superadmin',
  admin: 'Admin',
  director: 'Direktor',
  hr: 'HR',
  teacher: 'Lehrer',
  engineer: 'Engineer',
}

const ROLE_COLORS: Record<string, string> = {
  ultraadmin: 'bg-red-100 text-red-800',
  superadmin: 'bg-purple-100 text-purple-800',
  admin: 'bg-blue-100 text-blue-800',
  director: 'bg-indigo-100 text-indigo-800',
  hr: 'bg-yellow-100 text-yellow-800',
  teacher: 'bg-green-100 text-green-800',
  engineer: 'bg-orange-100 text-orange-800',
}

const ROLE_OPTIONS = ['superadmin', 'admin', 'hr', 'director', 'teacher']

function formatUserName(u: { first_name?: string | null; last_name?: string | null }) {
  const last = u.last_name?.trim() || ''
  const first = u.first_name?.trim() || ''
  if (last && first) return `${last}, ${first}`
  return last || first || '—'
}

function compareUsersByName(a: User, b: User) {
  const byLast = (a.last_name || '').localeCompare(b.last_name || '', 'de', { sensitivity: 'base' })
  if (byLast !== 0) return byLast
  return (a.first_name || '').localeCompare(b.first_name || '', 'de', { sensitivity: 'base' })
}

export function SuperadminManagementPage() {
  const [users, setUsers] = useState<User[]>([])
  const [loading, setLoading] = useState(true)
  const [showCreate, setShowCreate] = useState(false)
  const [form, setForm] = useState<UserFormData>(emptyForm)
  const [message, setMessage] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const [filterRole, setFilterRole] = useState<string>('')
  const [confirmDemote, setConfirmDemote] = useState<User | null>(null)

  const [searchParams, setSearchParams] = useSearchParams()
  const [activeTab, setActiveTab] = useState<'users' | 'bulk-import' | 'history'>(
    searchParams.get('tab') === 'bulk-import' ? 'bulk-import' : searchParams.get('tab') === 'history' ? 'history' : 'users'
  )

  const [history, setHistory] = useState<ImportSession[]>([])
  const [historyLoading, setHistoryLoading] = useState(false)
  const [selectedHistoryIds, setSelectedHistoryIds] = useState<Set<string>>(new Set())
  const [deletingHistory, setDeletingHistory] = useState(false)
  const [showHistoryDeleteConfirm, setShowHistoryDeleteConfirm] = useState(false)

  const [teacherFilters, setTeacherFilters] = useState({ search: '', min_ratio: '', max_ratio: '', min_accepts: '', max_accepts: '' })

  const [editingUser, setEditingUser] = useState<User | null>(null)
  const [editForm, setEditForm] = useState({
    first_name: '', last_name: '', email: '', phone: '', mobile: '', teacher_code: '',
    faculty_ids: [] as string[],
    language: 'de', is_active: true,
  })
  const [editUserRoles, setEditUserRoles] = useState<string[]>([])
  const [newRoleToAdd, setNewRoleToAdd] = useState('')

  interface FacultyOption {
    id: string
    name: string
    code: string | null
    color: string | null
    is_active: boolean
  }
  const [faculties, setFaculties] = useState<FacultyOption[]>([])
  const [loadingFaculties, setLoadingFaculties] = useState(false)
  const [allBackgrounds, setAllBackgrounds] = useState<Background[]>([])
  const [editBackgroundIds, setEditBackgroundIds] = useState<string[]>([])
  const [loadingBackgrounds, setLoadingBackgrounds] = useState(false)

  const [selectedUserIds, setSelectedUserIds] = useState<Set<string>>(new Set())
  const [deleting, setDeleting] = useState(false)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [activating, setActivating] = useState(false)

  const LANGUAGE_OPTIONS = [
    { value: 'de', label: 'Deutsch' },
    { value: 'fr', label: 'Französisch' },
    { value: 'it', label: 'Italienisch' },
    { value: 'en', label: 'Englisch' },
  ]

  const fetchHistory = async () => {
    setHistoryLoading(true)
    try {
      const res = await apiClient.get<{ sessions: ImportSession[]; total: number }>('/api/v1/bulk-import/users/history?page=1&page_size=50')
      setHistory(res.data.sessions)
    } catch {
    } finally { setHistoryLoading(false) }
  }

  const handleDownloadErrorReport = async (sessionId: string) => {
    try {
      const resp = await apiClient.get(`/api/v1/bulk-import/users/history/${sessionId}/error-report`, {
        responseType: 'blob',
      })
      const url = URL.createObjectURL(resp.data)
      const a = document.createElement('a')
      a.href = url
      a.download = `import-errors-${sessionId}.csv`
      a.click()
      URL.revokeObjectURL(url)
    } catch {
      return
    }
  }

  useEffect(() => {
    if (activeTab === 'history') fetchHistory()
  }, [activeTab])

  const toggleHistorySession = (id: string) => {
    setSelectedHistoryIds(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const toggleAllHistory = () => {
    if (selectedHistoryIds.size === history.length && history.length > 0) {
      setSelectedHistoryIds(new Set())
    } else {
      setSelectedHistoryIds(new Set(history.map(s => s.id)))
    }
  }

  const handleBulkDeleteHistory = async () => {
    if (selectedHistoryIds.size === 0) return
    setDeletingHistory(true)
    setError(null)
    try {
      await apiClient.post('/api/v1/bulk-import/users/history/bulk-delete', {
        session_ids: Array.from(selectedHistoryIds)
      })
      setMessage(`${selectedHistoryIds.size} Import-Session(s) gelöscht.`)
      setSelectedHistoryIds(new Set())
      setShowHistoryDeleteConfirm(false)
      await fetchHistory()
    } catch (err: unknown) {
      const errorMessage = extractErrorMessage(err, { loadError: 'Fehler beim Löschen' })
      setError(errorMessage)
    } finally {
      setDeletingHistory(false)
    }
  }

  const fetchUsers = async () => {
    setLoading(true)
    try {
      const params = new URLSearchParams()
      if (filterRole) params.set('role', filterRole)
      if (filterRole === 'teacher') {
        if (teacherFilters.search) params.set('search', teacherFilters.search)
        if (teacherFilters.min_ratio) params.set('min_ratio', teacherFilters.min_ratio)
        if (teacherFilters.max_ratio) params.set('max_ratio', teacherFilters.max_ratio)
        if (teacherFilters.min_accepts) params.set('min_accepts', teacherFilters.min_accepts)
        if (teacherFilters.max_accepts) params.set('max_accepts', teacherFilters.max_accepts)
      }
      const res = await apiClient.get<ApiUsersResponse>(`/api/v1/superadmin/users?${params}`)
      setUsers([...res.data.users].sort(compareUsersByName))
    } catch (err: unknown) {
      const errorMessage = extractErrorMessage(err, {
        loadError: 'Benutzer konnten nicht geladen werden',
        permissionError: 'Keine Berechtigung. Superadmin-Rolle erforderlich.',
        networkError: 'Server nicht erreichbar.',
      })
      console.error('Failed to fetch users:', err)
      setError(errorMessage)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { setSelectedUserIds(new Set()); fetchUsers() }, [filterRole])

  useEffect(() => {
    if (!message) return
    const t = setTimeout(() => setMessage(null), 5000)
    return () => clearTimeout(t)
  }, [message])

  useEffect(() => {
    if (activeTab !== 'users') {
      setShowCreate(false)
      setEditingUser(null)
      setConfirmDemote(null)
      setSelectedUserIds(new Set())
      setShowDeleteConfirm(false)
      setSelectedHistoryIds(new Set())
      setShowHistoryDeleteConfirm(false)
    }
  }, [activeTab])

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    setSubmitting(true)
    setError(null)
    try {
      if (form.role === 'superadmin') {
        await apiClient.post('/api/v1/superadmin/users/promote-superadmin', {
          email: form.email,
          first_name: form.first_name,
          last_name: form.last_name,
        })
      } else {
        await apiClient.post('/api/v1/superadmin/users/role', form)
      }
      setShowCreate(false)
      setForm(emptyForm)
      setMessage('Benutzer erstellt.')
      await fetchUsers()
    } catch (err: unknown) {
      const errorMessage = extractErrorMessage(err, { loadError: 'Fehler beim Erstellen' })
      setError(errorMessage)
    } finally {
      setSubmitting(false)
    }
  }

  const handleDemote = async (user: User) => {
    setSubmitting(true)
    setError(null)
    try {
      await apiClient.delete(`/api/v1/superadmin/users/${user.id}/role`)
      setMessage(`"${user.email}" wurde Rolle entfernt.`)
      setConfirmDemote(null)
      await fetchUsers()
    } catch (err: unknown) {
      const errorMessage = extractErrorMessage(err, { loadError: 'Fehler beim Demoten' })
      setError(errorMessage)
    } finally {
      setSubmitting(false)
    }
  }

  const handleToggleActive = async (user: User) => {
    setSubmitting(true)
    setError(null)
    try {
      if (user.is_active) {
        await apiClient.patch(`/api/v1/superadmin/users/${user.id}`, { is_active: false })
        setMessage(`"${user.email}" wurde deaktiviert.`)
      } else {
        await apiClient.patch(`/api/v1/superadmin/users/${user.id}`, { is_active: true })
        setMessage(`"${user.email}" wurde aktiviert.`)
      }
      await fetchUsers()
    } catch (err: unknown) {
      const errorMessage = extractErrorMessage(err, { loadError: 'Fehler beim Ändern des Status' })
      setError(errorMessage)
    } finally {
      setSubmitting(false)
    }
  }

  const openEdit = (user: User) => {
    setEditingUser(user)
    setEditForm({
      first_name: user.first_name,
      last_name: user.last_name,
      email: user.email,
      phone: user.phone || '',
      mobile: user.mobile || '',
      teacher_code: user.user_code || user.teacher_code || '',
      faculty_ids: user.faculties ? user.faculties.map(f => f.id) : [],
      language: user.language || 'de',
      is_active: user.is_active,
    })
    const roles = user.roles?.length > 0 ? user.roles : (user.role ? [user.role] : [])
    setEditUserRoles(roles)
    setNewRoleToAdd('')
    setEditBackgroundIds(user.backgrounds ? user.backgrounds.map(b => b.id) : [])
    if (user.teacher_id && faculties.length === 0) {
      loadFaculties()
    }
    if (user.teacher_id && allBackgrounds.length === 0) {
      loadBackgrounds()
    }
  }

  const loadFaculties = async () => {
    if (loadingFaculties || faculties.length > 0) return
    setLoadingFaculties(true)
    try {
      const res = await apiClient.get<FacultyOption[]>('/api/v1/teachers/faculties')
      setFaculties(res.data)
    } catch (err) {
      console.error('Fakultäten laden fehlgeschlagen:', err)
    } finally {
      setLoadingFaculties(false)
    }
  }

  const loadBackgrounds = async () => {
    if (loadingBackgrounds || allBackgrounds.length > 0) return
    setLoadingBackgrounds(true)
    try {
      const bgData = await listBackgrounds()
      setAllBackgrounds(bgData)
    } catch (err) {
      console.error('Hintergründe laden fehlgeschlagen:', err)
    } finally {
      setLoadingBackgrounds(false)
    }
  }

  const handleEditSave = async (e: FormEvent) => {
    e.preventDefault()
    if (!editingUser) return
    setSubmitting(true)
    setError(null)
    try {
      await apiClient.patch(`/api/v1/superadmin/users/${editingUser.id}`, {
        first_name: editForm.first_name || undefined,
        last_name: editForm.last_name || undefined,
        email: editForm.email || undefined,
        phone: editForm.phone || undefined,
        mobile: editForm.mobile || undefined,
        teacher_code: editForm.teacher_code || undefined,
        faculty_ids: editForm.faculty_ids.length > 0 ? editForm.faculty_ids : undefined,
        background_ids: editBackgroundIds.length > 0 ? editBackgroundIds : undefined,
        language: editForm.language || undefined,
        is_active: editForm.is_active,
      })
      setMessage(`"${editingUser.email}" aktualisiert.`)
      setEditingUser(null)
      await fetchUsers()
    } catch (err: unknown) {
      const errorMessage = extractErrorMessage(err, { loadError: 'Fehler beim Aktualisieren' })
      setError(errorMessage)
    } finally {
      setSubmitting(false)
    }
  }

  const handleAddRole = async () => {
    if (!editingUser || !newRoleToAdd) return
    if (editUserRoles.includes(newRoleToAdd)) {
      setError('Benutzer hat diese Rolle bereits.')
      return
    }
    setSubmitting(true)
    setError(null)
    try {
      await apiClient.post('/api/v1/superadmin/users/role', {
        email: editingUser.email,
        first_name: editingUser.first_name,
        last_name: editingUser.last_name,
        role: newRoleToAdd,
      })
      setMessage(`Rolle "${ROLE_LABELS[newRoleToAdd]}" hinzugefügt.`)
      setEditUserRoles(prev => [...prev, newRoleToAdd])
      setNewRoleToAdd('')
      await fetchUsers()
    } catch (err: unknown) {
      const errorMessage = extractErrorMessage(err, { loadError: 'Fehler beim Hinzufügen der Rolle' })
      setError(errorMessage)
    } finally {
      setSubmitting(false)
    }
  }

  const handleRemoveRole = async (roleToRemove: string) => {
    if (!editingUser) return
    const protectedRoles = ['superadmin', 'admin', 'teacher']
    if (protectedRoles.includes(roleToRemove) && editUserRoles.length <= 1) {
      setError('Benutzer muss mindestens eine Rolle haben.')
      return
    }
    setSubmitting(true)
    setError(null)
    try {
      await apiClient.delete(`/api/v1/superadmin/users/${editingUser.id}/role`, {
        params: { role: roleToRemove }
      })
      setMessage(`Rolle "${ROLE_LABELS[roleToRemove]}" entfernt.`)
      setEditUserRoles(prev => prev.filter(r => r !== roleToRemove))
      await fetchUsers()
    } catch (err: unknown) {
      const errorMessage = extractErrorMessage(err, { loadError: 'Fehler beim Entfernen der Rolle' })
      setError(errorMessage)
    } finally {
      setSubmitting(false)
    }
  }


  const handleImportComplete = (_result: ImportExecuteResult) => {
    fetchUsers()
  }

  const toggleUser = (id: string) => {
    setSelectedUserIds(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const toggleAll = (userList: User[]) => {
    if (selectedUserIds.size === userList.length && userList.length > 0) {
      setSelectedUserIds(new Set())
    } else {
      setSelectedUserIds(new Set(userList.map(u => u.id)))
    }
  }

  const clearSelection = () => setSelectedUserIds(new Set())

  const handleBulkDelete = async () => {
    if (selectedUserIds.size === 0) return
    setDeleting(true)
    setError(null)
    try {
      const res = await apiClient.post('/api/v1/superadmin/users/bulk-delete', {
        user_ids: Array.from(selectedUserIds)
      })
      const { deleted_count, total_timetable_entries_deleted } = res.data
      const blocked = res.data.results?.filter((r: { status: string }) => r.status === 'blocked')?.length || 0
      
      let message = `${deleted_count} Benutzer gelöscht.`
      if (total_timetable_entries_deleted > 0) {
        message += ` ${total_timetable_entries_deleted} Stundenplan-Einträge entfernt.`
      }
      if (blocked > 0) {
        message += ` ${blocked} aufgrund offener Vertretungen blockiert.`
      }
      setMessage(message)
      
      clearSelection()
      setShowDeleteConfirm(false)
      await fetchUsers()
    } catch (err: unknown) {
      const errorMessage = extractErrorMessage(err, { loadError: 'Fehler beim Löschen' })
      setError(errorMessage)
    } finally {
      setDeleting(false)
    }
  }

  const handleBulkActivate = async () => {
    if (selectedUserIds.size === 0) return
    setActivating(true)
    setError(null)
    try {
      const res = await apiClient.post('/api/v1/superadmin/users/bulk-activate', {
        user_ids: Array.from(selectedUserIds)
      })
      const { activated_count } = res.data
      setMessage(`${activated_count} Benutzer aktiviert.`)
      clearSelection()
      await fetchUsers()
    } catch (err: unknown) {
      const errorMessage = extractErrorMessage(err, { loadError: 'Fehler beim Aktivieren' })
      setError(errorMessage)
    } finally {
      setActivating(false)
    }
  }

  return (
    <div className="max-w-7xl mx-auto p-4 sm:p-6 lg:p-8">

      {message && (
        <div className="mb-4 p-3 bg-green-100 text-green-700 rounded flex items-center justify-between">
          <span>{message}</span>
          <button onClick={() => setMessage(null)} className="text-green-500 hover:text-green-700">x</button>
        </div>
      )}
      {error && (
        <div className="mb-4 p-3 bg-red-100 text-red-700 rounded flex items-center justify-between">
          <span>{error}</span>
          <button onClick={() => setError(null)} className="text-red-500 hover:text-red-700">x</button>
        </div>
      )}

      <div className="flex gap-2 mb-6 border-b">
        {[
          { key: 'users' as const, label: 'Benutzer' },
          { key: 'bulk-import' as const, label: 'Bulk Import' },
          { key: 'history' as const, label: 'Verlauf' },
        ].map(tab => (
          <button
            key={tab.key}
            onClick={() => {
              setActiveTab(tab.key)
              setSearchParams(tab.key === 'users' ? {} : { tab: tab.key }, { replace: true })
            }}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${activeTab === tab.key ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {activeTab === 'users' && (
        <>
          {loading ? (
            <div className="flex items-center justify-center min-h-[60vh]"><p className="text-gray-500">Laden...</p></div>
          ) : (
            <>
              <div className="flex justify-end mb-6">
            <button
              onClick={() => { setShowCreate(true); setForm(emptyForm); setError(null) }}
              className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
            >
              + Benutzer erstellen
            </button>
          </div>

          {/* Filter tabs */}
          <div className="flex flex-wrap gap-2 mb-4">
            <button
              onClick={() => setFilterRole('')}
              className={`px-3 py-1.5 text-sm rounded-md border ${!filterRole ? 'bg-gray-100 border-gray-300 font-medium' : 'bg-white border-gray-200'}`}
            >
              Alle
            </button>
            {['superadmin', 'admin', 'hr', 'director', 'teacher'].map((r) => (
              <button
                key={r}
                onClick={() => setFilterRole(r)}
                className={`px-3 py-1.5 text-sm rounded-md border ${filterRole === r ? 'bg-gray-100 border-gray-300 font-medium' : 'bg-white border-gray-200'}`}
              >
                {ROLE_LABELS[r]}
              </button>
            ))}
          </div>

          {selectedUserIds.size > 0 && (
            <div className="flex items-center gap-3 mb-4 p-3 bg-gray-50 rounded-lg">
              <span className="text-sm text-gray-700">{selectedUserIds.size} Benutzer ausgewählt</span>
              <button
                onClick={handleBulkActivate}
                disabled={activating || deleting}
                className="px-4 py-2 bg-green-600 text-white text-sm rounded-md hover:bg-green-700 disabled:opacity-40"
              >
                {activating ? 'Aktivieren...' : 'Aktivieren'}
              </button>
              <button
                onClick={() => setShowDeleteConfirm(true)}
                disabled={deleting || activating}
                className="px-4 py-2 bg-red-600 text-white text-sm rounded-md hover:bg-red-700 disabled:opacity-40"
              >
                {deleting ? 'Löschen...' : 'Löschen'}
              </button>
              <button
                onClick={clearSelection}
                disabled={deleting || activating}
                className="px-4 py-2 border border-gray-300 text-sm rounded-md hover:bg-gray-100 disabled:opacity-40"
              >
                Abbrechen
              </button>
            </div>
          )}

          {/* Teacher-style filters */}
          {filterRole === 'teacher' && (
            <div className="bg-white rounded-lg shadow p-4 mb-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">Suche</label>
                <input value={teacherFilters.search} onChange={(e) => setTeacherFilters({ ...teacherFilters, search: e.target.value })} placeholder="Name, Code" className="mt-1 block w-full rounded-md border-gray-300 shadow-sm px-3 py-2 border" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Min. Ratio (%)</label>
                <input type="number" min={0} max={100} value={teacherFilters.min_ratio} onChange={(e) => setTeacherFilters({ ...teacherFilters, min_ratio: e.target.value })} className="mt-1 block w-full rounded-md border-gray-300 shadow-sm px-3 py-2 border" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Max. Ratio (%)</label>
                <input type="number" min={0} max={100} value={teacherFilters.max_ratio} onChange={(e) => setTeacherFilters({ ...teacherFilters, max_ratio: e.target.value })} className="mt-1 block w-full rounded-md border-gray-300 shadow-sm px-3 py-2 border" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Min. Ann.</label>
                <input type="number" min={0} value={teacherFilters.min_accepts} onChange={(e) => setTeacherFilters({ ...teacherFilters, min_accepts: e.target.value })} className="mt-1 block w-full rounded-md border-gray-300 shadow-sm px-3 py-2 border" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Max. Ann.</label>
                <input type="number" min={0} value={teacherFilters.max_accepts} onChange={(e) => setTeacherFilters({ ...teacherFilters, max_accepts: e.target.value })} className="mt-1 block w-full rounded-md border-gray-300 shadow-sm px-3 py-2 border" />
              </div>
              <div className="sm:col-span-2 lg:col-span-5 flex flex-wrap gap-2">
                <button onClick={fetchUsers} className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700">Filtern</button>
                <button onClick={() => { setTeacherFilters({ search: '', min_ratio: '', max_ratio: '', min_accepts: '', max_accepts: '' }); fetchUsers() }} className="px-4 py-2 border border-gray-300 rounded-md">Zurücksetzen</button>
              </div>
            </div>
          )}

          <div className="bg-white rounded-lg shadow overflow-hidden">
            <div className="overflow-x-auto">
            {filterRole === 'teacher' ? (
              <table className="min-w-full divide-y divide-gray-200 whitespace-nowrap">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 w-10">
                      <input
                        type="checkbox"
                        checked={users.length > 0 && selectedUserIds.size === users.length}
                        onChange={() => toggleAll(users)}
                        className="rounded border-gray-300"
                      />
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">E-Mail</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Code</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Fakultat</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Facher</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Hintergrund</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Stundenplan</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Ratio</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Ann.</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Abl.</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Aktionen</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {users.length === 0 ? (
                    <tr><td colSpan={13} className="px-6 py-8 text-center text-gray-500">Keine Lehrer gefunden</td></tr>
                  ) : (
                    users.map((u) => (
                      <tr key={u.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4">
                          <input
                            type="checkbox"
                            checked={selectedUserIds.has(u.id)}
                            onChange={() => toggleUser(u.id)}
                            className="rounded border-gray-300"
                          />
                        </td>
                        <td className="px-6 py-4">{formatUserName(u)}</td>
                        <td className="px-6 py-4 text-sm text-gray-600">{u.email}</td>
                        <td className="px-6 py-4">{u.user_code || u.teacher_code || '-'}</td>
                        <td className="px-6 py-4">{u.faculty_name || '-'}</td>
                        <td className="px-6 py-4 text-sm">
                          {u.subjects && u.subjects.length > 0
                            ? u.subjects.map(s => s.name).join(', ')
                            : '-'}
                        </td>
                        <td className="px-6 py-4 text-sm">
                          {u.backgrounds && u.backgrounds.length > 0 
                            ? [...new Set(u.backgrounds.map(b => b.name))].join(', ') 
                            : '-'}
                        </td>
                        <td className="px-6 py-4">
                          {u.has_timetable !== undefined && u.has_timetable !== null ? (
                            <div className="flex items-center gap-1.5">
                              {u.has_timetable ? (
                                <>
                                  <CheckCircle2 className="h-4 w-4 text-green-500 flex-shrink-0" />
                                  <span className="text-xs text-green-700 font-medium">
                                    {u.timetable_entry_count} Einträge
                                  </span>
                                </>
                              ) : (
                                <>
                                  <XCircle className="h-4 w-4 text-red-400 flex-shrink-0" />
                                  <span className="text-xs text-gray-400">Keiner</span>
                                </>
                              )}
                            </div>
                          ) : (
                            <span className="text-xs text-gray-300">—</span>
                          )}
                        </td>
                        <td className="px-6 py-4">
                          {u.total_accepts + u.total_declines > 0
                            ? <span className={`px-2 py-1 text-xs rounded-full ${u.accept_decline_ratio >= 70 ? 'bg-green-100 text-green-800' : u.accept_decline_ratio >= 40 ? 'bg-yellow-100 text-yellow-800' : 'bg-red-100 text-red-800'}`}>
                                {u.accept_decline_ratio.toFixed(1)}%
                              </span>
                            : <span className="text-gray-400">-</span>}
                        </td>
                        <td className="px-6 py-4">{u.total_accepts}</td>
                        <td className="px-6 py-4">{u.total_declines}</td>
                        <td className="px-6 py-4">
                          <span className={`px-2 py-1 text-xs rounded-full ${u.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                            {u.is_active ? 'Aktiv' : 'Inaktiv'}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-sm space-x-2 text-right">
                          <button onClick={() => openEdit(u)} className="text-blue-600 hover:text-blue-900">Bearbeiten</button>
                          {u.is_active && <button onClick={() => handleToggleActive(u)} disabled={submitting} className="text-red-600 hover:text-red-900 disabled:opacity-40">Deaktivieren</button>}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            ) : (
              <table className="min-w-full divide-y divide-gray-200 whitespace-nowrap">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 w-10">
                      <input
                        type="checkbox"
                        checked={users.length > 0 && selectedUserIds.size === users.length}
                        onChange={() => toggleAll(users)}
                        className="rounded border-gray-300"
                      />
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">E-Mail</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Code</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Rolle</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Stundenplan</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Aktionen</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {users.length === 0 ? (
                    <tr><td colSpan={8} className="px-6 py-8 text-center text-gray-500">Keine Benutzer gefunden</td></tr>
                  ) : (
                    users.map((u) => (
                      <tr key={u.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4">
                          <input
                            type="checkbox"
                            checked={selectedUserIds.has(u.id)}
                            onChange={() => toggleUser(u.id)}
                            className="rounded border-gray-300"
                          />
                        </td>
                        <td className="px-6 py-4">{formatUserName(u)}</td>
                        <td className="px-6 py-4 text-sm text-gray-600">{u.email}</td>
                        <td className="px-6 py-4">{u.user_code || u.teacher_code || '-'}</td>
                        <td className="px-6 py-4">
                          <div className="flex flex-wrap gap-1">
                            {(u.roles.length > 0 ? u.roles : [u.role]).map((r) => (
                              <span key={r} className={`px-2 py-1 text-xs rounded-full font-medium ${ROLE_COLORS[r] || 'bg-gray-100 text-gray-600'}`}>
                                {ROLE_LABELS[r] || r}
                              </span>
                            ))}
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          {u.has_timetable !== undefined && u.has_timetable !== null ? (
                            <div className="flex items-center gap-1.5">
                              {u.has_timetable ? (
                                <>
                                  <CheckCircle2 className="h-4 w-4 text-green-500 flex-shrink-0" />
                                  <span className="text-xs text-green-700 font-medium">
                                    {u.timetable_entry_count} Einträge
                                  </span>
                                </>
                              ) : (
                                <>
                                  <XCircle className="h-4 w-4 text-gray-300 flex-shrink-0" />
                                  <span className="text-xs text-gray-400">—</span>
                                </>
                              )}
                            </div>
                          ) : (
                            <span className="text-xs text-gray-300">—</span>
                          )}
                        </td>
                        <td className="px-6 py-4">
                          <span className={`px-2 py-1 text-xs rounded-full ${u.is_active ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`}>
                            {u.is_active ? 'Aktiv' : 'Inaktiv'}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex flex-wrap gap-2 justify-end">
                            <button onClick={() => openEdit(u)} disabled={submitting} className="px-3 py-1 text-sm text-blue-600 hover:text-blue-900 border border-blue-200 rounded disabled:opacity-40">Bearbeiten</button>
                            {u.is_active ? (
                              <>
                                <button onClick={() => handleToggleActive(u)} disabled={submitting} className="px-3 py-1 text-sm text-red-600 hover:text-red-900 border border-red-200 rounded disabled:opacity-40">Deaktivieren</button>
                                {u.roles.includes('teacher') && u.roles.length <= 1 ? null : (
                                  <button onClick={() => setConfirmDemote(u)} disabled={submitting} className="px-3 py-1 text-sm text-orange-600 hover:text-orange-900 border border-orange-200 rounded disabled:opacity-40">Rolle entfernen</button>
                                )}
                              </>
                            ) : (
                              <button onClick={() => handleToggleActive(u)} disabled={submitting} className="px-3 py-1 text-sm text-green-600 hover:text-green-900 border border-green-200 rounded disabled:opacity-40">Aktivieren</button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            )}
            </div>
          </div>

          {/* Create Modal */}
          {showCreate && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
              <div className="bg-white rounded-lg p-6 w-full max-w-md">
                <h2 className="text-xl font-bold mb-4">Benutzer erstellen</h2>
                <form onSubmit={handleSubmit}>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">E-Mail *</label>
                      <input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} type="email" required className="mt-1 block w-full rounded-md border border-gray-300 shadow-sm px-3 py-2" />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Vorname *</label>
                        <input value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} type="text" required className="mt-1 block w-full rounded-md border border-gray-300 shadow-sm px-3 py-2" />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Nachname *</label>
                        <input value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} type="text" required className="mt-1 block w-full rounded-md border border-gray-300 shadow-sm px-3 py-2" />
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Rolle *</label>
                      <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className="mt-1 block w-full rounded-md border border-gray-300 shadow-sm px-3 py-2">
                        {ROLE_OPTIONS.map((r) => (
                          <option key={r} value={r}>{ROLE_LABELS[r]}</option>
                        ))}
                      </select>
                      <p className="text-xs text-gray-500 mt-1">Benutzer können später weitere Rollen erhalten.</p>
                    </div>
                    {form.role === 'teacher' && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Lehrer-Code</label>
                        <input value={form.teacher_code} onChange={(e) => setForm({ ...form, teacher_code: e.target.value })} type="text" className="mt-1 block w-full rounded-md border border-gray-300 shadow-sm px-3 py-2" />
                      </div>
                    )}
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Telefon</label>
                      <input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} type="tel" className="mt-1 block w-full rounded-md border border-gray-300 shadow-sm px-3 py-2" />
                    </div>
                  </div>
                  <div className="mt-6 flex justify-end space-x-3">
                    <button type="button" onClick={() => setShowCreate(false)} className="px-4 py-2 border border-gray-300 rounded-md">Abbrechen</button>
                    <button type="submit" disabled={submitting} className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50">{submitting ? 'Erstellen...' : 'Erstellen'}</button>
                  </div>
                </form>
              </div>
            </div>
          )}

          {/* Edit Modal */}
          {editingUser && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
              <div className="bg-white rounded-lg p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto">
                <h2 className="text-xl font-bold mb-4">Benutzer bearbeiten</h2>
                <form onSubmit={handleEditSave}>
                  <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Vorname</label>
                        <input value={editForm.first_name} onChange={(e) => setEditForm({ ...editForm, first_name: e.target.value })} type="text" className="mt-1 block w-full rounded-md border border-gray-300 shadow-sm px-3 py-2" />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Nachname</label>
                        <input value={editForm.last_name} onChange={(e) => setEditForm({ ...editForm, last_name: e.target.value })} type="text" className="mt-1 block w-full rounded-md border border-gray-300 shadow-sm px-3 py-2" />
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">E-Mail *</label>
                      <input value={editForm.email} onChange={(e) => setEditForm({ ...editForm, email: e.target.value })} type="email" required className="mt-1 block w-full rounded-md border border-gray-300 shadow-sm px-3 py-2" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Telefon</label>
                      <input value={editForm.phone} onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })} type="tel" className="mt-1 block w-full rounded-md border border-gray-300 shadow-sm px-3 py-2" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Mobil</label>
                      <input value={editForm.mobile} onChange={(e) => setEditForm({ ...editForm, mobile: e.target.value })} type="tel" className="mt-1 block w-full rounded-md border border-gray-300 shadow-sm px-3 py-2" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Rollen</label>
                      <div className="flex flex-wrap gap-2 mb-2 min-h-[32px]">
                        {editUserRoles.map(r => (
                          <span key={r} className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-sm ${ROLE_COLORS[r] || 'bg-gray-100 text-gray-600'}`}>
                            {ROLE_LABELS[r] || r}
                            {editUserRoles.length > 1 && (
                              <button type="button" onClick={() => handleRemoveRole(r)} className="ml-1 hover:text-red-600" aria-label={`${ROLE_LABELS[r]} entfernen`}>×</button>
                            )}
                          </span>
                        ))}
                      </div>
                      <div className="flex gap-2">
                        <select
                          value={newRoleToAdd}
                          onChange={(e) => setNewRoleToAdd(e.target.value)}
                          className="block w-full rounded-md border border-gray-300 shadow-sm px-3 py-2 bg-white"
                        >
                          <option value="">+ Rolle hinzufügen...</option>
                          {ROLE_OPTIONS.filter(r => !editUserRoles.includes(r)).map(r => (
                            <option key={r} value={r}>{ROLE_LABELS[r]}</option>
                          ))}
                        </select>
                        <button
                          type="button"
                          onClick={handleAddRole}
                          disabled={!newRoleToAdd || submitting}
                          className="px-3 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
                        >
                          Hinzufügen
                        </button>
                      </div>
                    </div>
                    <>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Lehrer-Code</label>
                        <input value={editForm.teacher_code} onChange={(e) => setEditForm({ ...editForm, teacher_code: e.target.value })} type="text" className="mt-1 block w-full rounded-md border border-gray-300 shadow-sm px-3 py-2" />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Fachbereiche</label>
                        <div className="flex flex-wrap gap-2 mb-2 min-h-[32px]">
                          {editForm.faculty_ids.map(fid => {
                            const fac = faculties.find(f => f.id === fid)
                            if (!fac) return null
                            const isPrimary = editForm.faculty_ids[0] === fid
                            return (
                              <span key={fid} className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-sm ${isPrimary ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-700'}`}>
                                {fac.name}
                                {isPrimary && <span className="text-xs opacity-60">★</span>}
                                <button type="button" onClick={() => {
                                  setEditForm(prev => ({ ...prev, faculty_ids: prev.faculty_ids.filter(id => id !== fid) }))
                                }} className="ml-1 hover:text-red-600" aria-label={`${fac.name} entfernen`}>×</button>
                              </span>
                            )
                          })}
                          {editForm.faculty_ids.length === 0 && (
                            <span className="text-sm text-gray-400 italic">Keine Fachbereiche zugewiesen</span>
                          )}
                        </div>
                        <select
                          value=""
                          onChange={(e) => {
                            if (e.target.value && !editForm.faculty_ids.includes(e.target.value)) {
                              setEditForm(prev => ({ ...prev, faculty_ids: [...prev.faculty_ids, e.target.value] }))
                            }
                          }}
                          className="block w-full rounded-md border border-gray-300 shadow-sm px-3 py-2 bg-white"
                        >
                          <option value="">+ Fachbereich hinzufügen...</option>
                          {faculties
                            .filter(f => f.is_active && !editForm.faculty_ids.includes(f.id))
                            .map(f => <option key={f.id} value={f.id}>{f.name}</option>)}
                        </select>
                        {loadingFaculties && <p className="text-xs text-gray-400 mt-1">Laden...</p>}
                        {editForm.faculty_ids.length > 0 && (
                          <p className="text-xs text-gray-400 mt-1">Erster Fachbereich (★) = Primärer Bereich</p>
                        )}
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Sprache</label>
                        <select value={editForm.language} onChange={(e) => setEditForm({ ...editForm, language: e.target.value })} className="mt-1 block w-full rounded-md border border-gray-300 shadow-sm px-3 py-2 bg-white">
                          {LANGUAGE_OPTIONS.map(opt => (
                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                          ))}
                        </select>
                      </div>
                    </>
                    {editingUser.subjects && editingUser.subjects.length > 0 && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Fach / Fächer</label>
                        <div className="mt-1 flex flex-wrap gap-1">
                          {editingUser.subjects.map(s => (
                            <span key={s.id} className="px-2 py-1 text-xs rounded-full bg-gray-100">{s.name}</span>
                          ))}
                        </div>
                      </div>
                    )}
                    {editingUser.faculty_name && (!editingUser.subjects || editingUser.subjects.length === 0) && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Aktueller Fachbereich</label>
                        <div className="mt-1 text-sm text-gray-700">{editingUser.faculty_name}</div>
                      </div>
                    )}
                    {editingUser.teacher_id && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Hintergründe</label>
                        <div className="flex flex-wrap gap-2 mb-2 min-h-[32px]">
                          {editBackgroundIds.map(bgId => {
                            const bg = allBackgrounds.find(b => b.id === bgId)
                            if (!bg) return null
                            const isPrimary = editBackgroundIds[0] === bgId
                            return (
                              <span key={bgId} className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-sm ${isPrimary ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-700'}`}>
                                {bg.name}
                                {bg.code && <span className="text-xs opacity-60">({bg.code})</span>}
                                {isPrimary && <span className="text-xs opacity-60">★</span>}
                                <button type="button" onClick={() => {
                                  setEditBackgroundIds(editBackgroundIds.filter(id => id !== bgId))
                                }} className="ml-1 hover:text-red-600" aria-label={`${bg.name} entfernen`}>×</button>
                              </span>
                            )
                          })}
                          {editBackgroundIds.length === 0 && (
                            <span className="text-sm text-gray-400 italic">Keine Hintergründe zugewiesen</span>
                          )}
                        </div>
                        <select
                          value=""
                          onChange={(e) => {
                            if (e.target.value && !editBackgroundIds.includes(e.target.value)) {
                              setEditBackgroundIds([...editBackgroundIds, e.target.value])
                            }
                          }}
                          className="block w-full rounded-md border border-gray-300 shadow-sm px-3 py-2 bg-white"
                        >
                          <option value="">+ Hintergrund hinzufügen...</option>
                          {allBackgrounds
                            .filter(b => b.is_active && !editBackgroundIds.includes(b.id))
                            .map(b => <option key={b.id} value={b.id}>{b.name} {b.code ? `(${b.code})` : ''}</option>)}
                        </select>
                        {loadingBackgrounds && <p className="text-xs text-gray-400 mt-1">Laden...</p>}
                        {editBackgroundIds.length > 0 && (
                          <p className="text-xs text-gray-400 mt-1">Erster Hintergrund (★) = Primärer Hintergrund</p>
                        )}
                      </div>
                    )}
                    {(editingUser.total_accepts > 0 || editingUser.total_declines > 0) && (
                      <div className="pt-2 border-t border-gray-200">
                        <label className="block text-sm font-medium text-gray-500 mb-1">Vertretungs-Statistiken</label>
                        <div className="grid grid-cols-4 gap-2 text-sm">
                          <div>
                            <span className="text-gray-500">Ratio:</span>{' '}
                            <span className={`px-1.5 py-0.5 text-xs rounded-full ${editingUser.accept_decline_ratio >= 70 ? 'bg-green-100 text-green-800' : editingUser.accept_decline_ratio >= 40 ? 'bg-yellow-100 text-yellow-800' : 'bg-red-100 text-red-800'}`}>
                              {editingUser.accept_decline_ratio.toFixed(1)}%
                            </span>
                          </div>
                          <div><span className="text-gray-500">Ann.:</span> {editingUser.total_accepts}</div>
                          <div><span className="text-gray-500">Abl.:</span> {editingUser.total_declines}</div>
                          <div><span className="text-gray-500">Akt. Sem.:</span> {editingUser.substitution_range_current_semester}</div>
                        </div>
                      </div>
                    )}
                    {/* NEU: Stundenplan Status Section */}
                    {editingUser.has_timetable !== undefined && editingUser.has_timetable !== null && (
                      <div className="pt-2 border-t border-gray-200">
                        <label className="block text-sm font-medium text-gray-500 mb-1">Stundenplan</label>
                        <div className="flex items-center gap-2">
                          {editingUser.has_timetable ? (
                            <>
                              <CheckCircle2 className="h-5 w-5 text-green-500" />
                              <span className="text-sm text-green-700 font-medium">
                                {editingUser.timetable_entry_count} Einträge vorhanden
                              </span>
                            </>
                          ) : (
                            <>
                              <XCircle className="h-5 w-5 text-red-400" />
                              <span className="text-sm text-gray-500">Kein Stundenplan importiert</span>
                            </>
                          )}
                        </div>
                        {editingUser.has_timetable && (
                          <p className="text-xs text-gray-400 mt-1">
                            ⚠️ Beim Löschen werden alle Stundenplan-Einträge entfernt.
                          </p>
                        )}
                      </div>
                    )}
                    <label className="flex items-center space-x-3">
                      <input type="checkbox" checked={editForm.is_active} onChange={(e) => setEditForm({ ...editForm, is_active: e.target.checked })} className="h-4 w-4" />
                      <span className="text-sm">Aktiv</span>
                    </label>
                  </div>
                  <div className="mt-6 flex justify-end space-x-3">
                    <button type="button" onClick={() => setEditingUser(null)} className="px-4 py-2 border border-gray-300 rounded-md">Abbrechen</button>
                    <button type="submit" disabled={submitting} className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50">{submitting ? 'Speichern...' : 'Speichern'}</button>
                  </div>
                </form>
              </div>
            </div>
          )}

          {/* Demote Confirmation */}
          {confirmDemote && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
              <div className="bg-white rounded-lg p-6 w-full max-w-md">
                <h2 className="text-lg font-bold mb-2">Rolle entfernen</h2>
                <p className="text-sm text-gray-600 mb-4">
                  Möchtest du eine Rolle von <strong>{confirmDemote.email}</strong> entfernen? Aktuell: <strong>{confirmDemote.roles.map(r => ROLE_LABELS[r] || r).join(', ')}</strong>.
                </p>
                <div className="flex justify-end space-x-3">
                  <button onClick={() => setConfirmDemote(null)} className="px-4 py-2 border border-gray-300 rounded-md">Abbrechen</button>
                  <button onClick={() => handleDemote(confirmDemote)} disabled={submitting} className="px-4 py-2 bg-orange-600 text-white rounded-md hover:bg-orange-700 disabled:opacity-50">Entfernen</button>
                </div>
              </div>
            </div>
          )}

          {/* Delete Confirmation with timetable warning */}
          <ConfirmDeleteModal
            isOpen={showDeleteConfirm}
            onClose={() => setShowDeleteConfirm(false)}
            onConfirm={handleBulkDelete}
            selectedUsers={users.filter(u => selectedUserIds.has(u.id))}
            isDeleting={deleting}
          />
            </>
          )}
        </>
      )}

      {activeTab === 'bulk-import' && (
        <BulkImportTab
          key={activeTab}
          onImportComplete={handleImportComplete}
        />
      )}

      {activeTab === 'history' && (
        <div>
          {selectedHistoryIds.size > 0 && (
            <div className="flex items-center gap-3 mb-4 p-3 bg-red-50 rounded-lg">
              <span className="text-sm text-red-700">{selectedHistoryIds.size} Sessionen ausgewählt</span>
              <button
                onClick={() => setShowHistoryDeleteConfirm(true)}
                disabled={deletingHistory}
                className="px-4 py-2 bg-red-600 text-white text-sm rounded-md hover:bg-red-700 disabled:opacity-40"
              >
                {deletingHistory ? 'Löschen...' : 'Löschen'}
              </button>
              <button
                onClick={() => setSelectedHistoryIds(new Set())}
                disabled={deletingHistory}
                className="px-4 py-2 border border-gray-300 text-sm rounded-md hover:bg-gray-50 disabled:opacity-40"
              >
                Abbrechen
              </button>
            </div>
          )}
          <div className="bg-white rounded-lg border overflow-hidden">
            {historyLoading ? <div className="p-6 text-gray-500">Laden...</div> : (
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 w-10">
                      <input
                        type="checkbox"
                        checked={history.length > 0 && selectedHistoryIds.size === history.length}
                        onChange={toggleAllHistory}
                        className="rounded border-gray-300"
                      />
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Datum</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Rolle</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Datei</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Erstellt</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Update</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Fehler</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                    <th className="px-4 py-3"></th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {history.length === 0 ? (
                    <tr><td colSpan={9} className="px-4 py-8 text-center text-gray-500">Keine Importe gefunden</td></tr>
                  ) : history.map(s => (
                    <tr key={s.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3">
                        <input
                          type="checkbox"
                          checked={selectedHistoryIds.has(s.id)}
                          onChange={() => toggleHistorySession(s.id)}
                          className="rounded border-gray-300"
                        />
                      </td>
                      <td className="px-4 py-3 text-xs">{new Date(s.created_at).toLocaleString('de-CH')}</td>
                      <td className="px-4 py-3 text-sm font-medium">{s.target_role}</td>
                      <td className="px-4 py-3 text-xs font-mono">{s.original_filename}</td>
                      <td className="px-4 py-3 text-sm text-green-600">{s.rows_created}</td>
                      <td className="px-4 py-3 text-sm text-blue-600">{s.rows_updated}</td>
                      <td className="px-4 py-3 text-sm text-red-600">{s.rows_errored}</td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-0.5 text-xs rounded-full ${s.status === 'completed' ? 'bg-green-100 text-green-700' : s.status === 'partial' ? 'bg-yellow-100 text-yellow-700' : 'bg-red-100 text-red-700'}`}>
                          {s.status}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        {s.rows_errored > 0 && (
                          <button
                            onClick={() => handleDownloadErrorReport(s.id)}
                            className="text-xs text-red-600 hover:underline"
                          >
                            Fehler-Report
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}

      {/* History Delete Confirmation */}
      {showHistoryDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h2 className="text-lg font-bold mb-2">Import-Sessions löschen</h2>
            <p className="text-sm text-gray-600 mb-4">
              Möchtest du <strong>{selectedHistoryIds.size} Import-Session(s)</strong> endgültig löschen? Diese Aktion kann nicht rückgängig gemacht werden.
            </p>
            <div className="flex justify-end space-x-3">
              <button onClick={() => setShowHistoryDeleteConfirm(false)} disabled={deletingHistory} className="px-4 py-2 border border-gray-300 rounded-md">Abbrechen</button>
              <button onClick={handleBulkDeleteHistory} disabled={deletingHistory} className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50">{deletingHistory ? 'Löschen...' : 'Löschen'}</button>
            </div>
          </div>
        </div>
      )}


      {/* Director V2: Pro-Direktor-Rechte (Kap. 8) */}
      <section className="mt-8 bg-white rounded-lg shadow p-6">
        <h2 className="text-lg font-semibold mb-1">Pro-Direktor-Rechte</h2>
        <p className="text-sm text-gray-500 mb-4">
          Festlegen, was dieser Direktor sehen und verändern darf (Superadmin-only).
        </p>
        <DirectorRightsEditor
          directors={users
            .filter(u => u.role === 'director')
            .map(u => ({ id: u.id, label: `${formatUserName(u)} (${u.email})` }))}
        />
      </section>

      </div>
  )
}
